import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import {
  PushNotificationSchema,
  PushNotifications,
  RegistrationError,
  Token,
} from '@capacitor/push-notifications';
import * as Sentry from '@sentry/capacitor';
import { History } from 'history';
import { getCurrentPosition } from 'hooks/useUserPosition';
import { registerDeviceWithWatchDuty } from 'shared/device';
import { User } from 'shared/types';
import {
  getPushTokenFromLocalStorage,
  setLastPushNotificationTimestamp,
} from 'state/localStorage';
import { debounce } from 'es-toolkit/compat';
import { Filesystem, Directory } from '@capacitor/filesystem';
import {
  getNotificationData,
  getRouteForPushData,
  initAndroidPushChannel,
  isVisiblePushNotification,
  replayAndroidPushInFocus,
} from './initPushNotifications.utils';

export const DEBOUNCE_REGISTER_DEVICE_MS = 100;

const debouncedRegisterDeviceWithWatchDuty = debounce(
  registerDeviceWithWatchDuty,
  DEBOUNCE_REGISTER_DEVICE_MS,
  { trailing: true },
);

/**
 * Delete FCM PersistedInstallation files to force token regeneration
 * This is necessary when the FCM token is restored from a previous device
 * and is not recognized as valid by the FCM server.
 * This function is only applicable for Android devices. @linear:ENG-3041
 *
 * see: https://github.com/firebase/firebase-android-sdk/issues/7025#issuecomment-3024585355
 * "Deleting the PersistedInstallation....json file on the error"
 */
const removeFirebasePersistedFile = async (): Promise<boolean> => {
  if (Capacitor.getPlatform() !== 'android') {
    return false;
  }

  try {
    const { files } = await Filesystem.readdir({
      path: '',
      directory: Directory.Data,
    });

    const fcmFiles = files.filter(
      (file) =>
        file.name.startsWith('PersistedInstallation.') &&
        file.name.endsWith('.json'),
    );

    if (fcmFiles.length === 0) return false;

    await Promise.all(
      fcmFiles.map((file) =>
        Filesystem.deleteFile({
          path: file.name,
          directory: Directory.Data,
        }),
      ),
    );

    return true;
  } catch (error) {
    Sentry.captureException(error);
    return false;
  }
};

export const handlePushRegistrationFinished = async (
  token: Token,
  user?: User | null,
): Promise<void> => {
  const currentToken = getPushTokenFromLocalStorage();

  // if the token given to us from FCM is the same as what
  // we have locally, this is a no-op
  if (currentToken === token.value) {
    return;
  }

  const [, positionResult] = await getCurrentPosition();
  const params = {
    user,
    push_token: token.value,
    lat: positionResult?.coords?.latitude,
    lng: positionResult?.coords?.longitude,
  };

  // The PushNotifications "registration" event is fired more than once on android,
  // so we need to debounce the device registration.
  await debouncedRegisterDeviceWithWatchDuty(params);
};

const handlePushRegistrationFailed = async (
  error: RegistrationError,
): Promise<void> => {
  // Check if the error is related to invalid FCM state from restore
  if (error?.error?.includes('Invalid argument for the given fid')) {
    Sentry.captureMessage('fcm-fid-error');
    // Delete FCM files and retry registration
    const cleared = await removeFirebasePersistedFile();

    // we successfully cleared the FCM files, so we can retry registration
    if (cleared) {
      Sentry.captureMessage('fcm-fid-cleared');
      await PushNotifications.register();
      return;
    }
    Sentry.captureMessage('fcm-fid-clearing-failed');
  }

  // @todo upload this token to watch duty
  console.error('push registration failed', error);
};

const handlePushReceived = async (
  notification: PushNotificationSchema,
  setCacheBuster?: (ts: number | null) => void,
): Promise<void> => {
  setLastPushNotificationTimestamp();

  if (Capacitor.getPlatform() === 'android') {
    /**
     * On Android, clear the auto-displayed foreground notification.
     * This is necessary to replace it with a custom local notification that is
     * interactive and uses the correct sound channel.
     */
    await PushNotifications.getDeliveredNotifications()
      .then((deliveredNotif) => {
        PushNotifications.removeDeliveredNotifications(deliveredNotif);
      })
      .catch((err) => Sentry.captureException(err));
  }

  if (!isVisiblePushNotification(notification)) {
    return;
  }

  const { object } = getNotificationData(notification);

  // make a cacheBuster from the model
  const ts =
    'date_modified' in object && object.date_modified
      ? new Date(object.date_modified)
      : new Date();
  if (setCacheBuster) setCacheBuster(ts.getTime());

  if (Capacitor.getPlatform() === 'android') {
    replayAndroidPushInFocus(notification);
  }
};

const handlePushTapped = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details: Record<string, any>,
  history: History,
  setCacheBuster?: (ts: number | null) => void,
  onClearMapSelectionsForRouterEntity?: () => void,
): void => {
  const { actionId, notification } = details;
  if (actionId !== 'tap') {
    return;
  }

  // Need this to handle the case of a notification being tapped while the app is not in the foreground
  setLastPushNotificationTimestamp();

  const pushData = getNotificationData(notification);

  // make a cacheBuster from the model
  const ts =
    'date_modified' in pushData.object && pushData.object.date_modified
      ? new Date(pushData.object.date_modified)
      : new Date();

  if (setCacheBuster) {
    setCacheBuster(ts.getTime());
  }

  const route = getRouteForPushData(pushData);

  if (route) {
    onClearMapSelectionsForRouterEntity?.();
    /**
     * Adds a location state to override the default map centering behavior when
     * navigating away from a progression model page.
     *
     * By default, redirecting from the progression model page to the main page
     * preserves the current map view.
     *
     * The `WildfireProgressionModel` page uses `useInterceptBackNavigation` to add
     * `{ skipMapCentering: true }` to the location state when redirecting to a
     * wildfire incident route, and if no location state is already defined.
     *
     * Any defined location state would technically bypass navigation interception,
     * but for consistency, `{ skipMapCentering: false }` is used.
     *
     * The `skipMapCentering` flag is then read by `useCenterMap` to determine whether
     * to trigger map centering.
     *
     * This ensures that when a user taps a push notification linking to a wildfire
     * incident, the map centers on the incident after redirection.
     */
    history.push(route, { skipMapCentering: false });
  }
};

export const initPushNotifications = async (
  history: History,
  setCacheBuster?: (ts: number | null) => void,
  handleTokenRegistration?: (token: Token) => Promise<void>,
  onClearMapSelectionsForRouterEntity?: () => void,
): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  if (!Capacitor.isPluginAvailable('PushNotifications')) {
    Sentry.captureMessage('pushnotifications-plugin-unavailable', {
      level: 'fatal',
    });
    return false;
  }

  const result = await PushNotifications.requestPermissions();

  if (result.receive !== 'granted') {
    return false;
  }

  await PushNotifications.removeAllListeners();

  // Note: these listeners need to come BEFORE PushNotifications.register()
  PushNotifications.addListener(
    'registration',
    handleTokenRegistration || handlePushRegistrationFinished,
  );
  PushNotifications.addListener(
    'registrationError',
    handlePushRegistrationFailed,
  );
  PushNotifications.addListener('pushNotificationReceived', (notif) => {
    handlePushReceived(notif, setCacheBuster);
  });
  PushNotifications.addListener('pushNotificationActionPerformed', (notif) => {
    handlePushTapped(
      notif,
      history,
      setCacheBuster,
      onClearMapSelectionsForRouterEntity,
    );
  });
  LocalNotifications.addListener(
    'localNotificationActionPerformed',
    (notif) => {
      handlePushTapped(
        notif,
        history,
        setCacheBuster,
        onClearMapSelectionsForRouterEntity,
      );
    },
  );

  // Register with Apple / Google to receive push token via APNS/FCM
  await PushNotifications.register();

  if (Capacitor.getPlatform() === 'android') {
    await initAndroidPushChannel();
  }

  return true;
};
